import { Button, Group, SegmentedControl, Select, Stack, Text, Tooltip } from '@mantine/core';
import { useState, type ReactNode } from 'react';
import type { EntityType } from '../../domain/entityTypes.js';
import { labelOf } from '../../domain/labels.js';
import { notComparedSentence, type NotComparedEntry } from '../../domain/notCompared.js';
import { countWord } from '../../domain/pluralise.js';
import { DiffGroups } from '../Diff/DiffGroups.js';
import { fieldDiffView } from './fieldDiffMode.js';
import type { EntityDetailState } from './useEntityDetail.js';

type Focused = EntityDetailState['focused'];

/**
 * Field-by-field, for one environment.
 *
 * Each case says the one thing that is true (`fieldDiffMode`), never one shrug across all of
 * them: no environment chosen; `missing`, previewed as the record a push would create there; an
 * environment the record is not targeted at, not pulled, or that only it has; and an environment
 * where everything already matches.
 *
 * The diff reads in the push's direction, the way the data moves: master's value, then the
 * environment's value now, the one a push would write over (`flow="source"`).
 *
 * `type` picks the field labels; without it a field reads as its name humanised.
 *
 * On a `drift` environment each differing field ends in one **Adopt ▾** (`AdoptFieldMenu`):
 * **Adopt as override** on a per-environment field (a URL, a game's status) stores the value as
 * that environment's override (`env == <name>`), master's default untouched; **Adopt to master**
 * on any field makes the value master's default for every environment, behind a dry-run confirm.
 */
export const FieldDiffPanel = ({
    type,
    environments,
    focus,
    onFocus,
    focused,
    notCompared,
    onReviewFullScreen,
    rowAction,
    onAdoptAllAsOverride,
    targets,
    editTargets,
    pullNow,
}: {
    type: EntityType;
    environments: string[];
    focus: string | undefined;
    onFocus: (environment: string | undefined) => void;
    focused: Focused;
    notCompared: NotComparedEntry[];
    /** Absent where the hosting page cannot push: the button is then not offered. */
    onReviewFullScreen?: () => void;
    /** One differing field's **Adopt ▾** — absent where master has no copy of the record: there is nothing to adopt into. */
    rowAction?: (group: { field: string; label: string }) => ReactNode;
    /** Absent where master has no copy of the record: there is nothing to override. */
    onAdoptAllAsOverride?: (environment: string, fields: string[]) => void;
    /** Master's targets selector, named on an `excluded` environment; absent when master has no copy. */
    targets?: string;
    /** The way to change `targets` (a link to the editor), offered on an `excluded` environment. */
    editTargets?: ReactNode;
    /** **Pull now** for the focused environment, offered when it is `unknown`. */
    pullNow?: ReactNode;
}) => {
    const [view, setView] = useState<'changed' | 'all'>('changed');
    const { mode, createGroups, groups, changed, diffFields, overridable, actionable } = fieldDiffView(
        type,
        focus,
        focused,
        view,
        Boolean(onAdoptAllAsOverride),
    );
    const canReview = onReviewFullScreen && actionable;
    return (
        <Stack gap="sm">
            <Group gap="sm" wrap="wrap" justify="space-between" align="flex-end">
                <Select
                    label="Environment"
                    data={environments}
                    value={focus ?? null}
                    onChange={(value) => onFocus(value ?? undefined)}
                    placeholder="Pick an environment"
                />
                {canReview && (
                    <Button size="compact-xs" variant="subtle" color="gray" onClick={onReviewFullScreen}>
                        Review full screen
                    </Button>
                )}
            </Group>

            {mode === 'compare' && (
                <SegmentedControl
                    size="xs"
                    w="fit-content"
                    value={view}
                    onChange={(next) => setView(next as 'changed' | 'all')}
                    data={[
                        { value: 'changed', label: 'Changed only' },
                        { value: 'all', label: 'All fields' },
                    ]}
                />
            )}

            {mode === 'pick' && (
                <Text c="dimmed" fz="sm">
                    Pick an environment to see which fields differ.
                </Text>
            )}

            {mode === 'excluded' && (
                <Group gap="sm" wrap="wrap" justify="space-between">
                    <Text c="dimmed" fz="sm">
                        <span className="rgs-numeric">{focus}</span> isn't in this record's targets
                        {targets && (
                            <>
                                {' '}
                                (<span className="rgs-numeric">{targets}</span>)
                            </>
                        )}
                        , so a push leaves it alone.
                    </Text>
                    {editTargets}
                </Group>
            )}

            {mode === 'unknown' && (
                <Group gap="sm" wrap="wrap" justify="space-between">
                    <Text c="dimmed" fz="sm">
                        <span className="rgs-numeric">{focus}</span> hasn't been pulled, or its last pull failed.
                    </Text>
                    {pullNow}
                </Group>
            )}

            {mode === 'env_only' && (
                <Text c="dimmed" fz="sm">
                    Only <span className="rgs-numeric">{focus}</span> has this record; master has no copy to compare.
                </Text>
            )}

            {mode === 'absent' && (
                <Text c="dimmed" fz="sm">
                    Nothing to compare in <span className="rgs-numeric">{focus}</span>.
                </Text>
            )}

            {createGroups && (
                <>
                    <Text fz="xs" c="dimmed">
                        Push to <span className="rgs-numeric">{focus}</span> would create this{' '}
                        {labelOf(type).singular.toLowerCase()}
                    </Text>
                    <DiffGroups
                        groups={createGroups}
                        afterOnly
                        headings={[
                            'Field',
                            null,
                            <span key="env">
                                <span className="rgs-numeric">{focus}</span> after push
                            </span>,
                        ]}
                    />
                </>
            )}

            {groups?.length === 0 && (
                <Text c="dimmed" fz="sm">
                    Every synced field matches.
                </Text>
            )}

            {groups && groups.length > 0 && changed > 0 && (
                <Group gap="sm" wrap="wrap" justify="space-between" mih={22}>
                    <Text fz="xs" c="dimmed">
                        Push to <span className="rgs-numeric">{focus}</span> would change {countWord(changed, 'field')}
                    </Text>
                    {overridable.length > 1 && (
                        <Tooltip
                            withArrow
                            label={`Only ${focus} keeps these values. Master's defaults and the other environments stay as they are.`}
                            multiline
                            w={260}
                        >
                            <Button
                                size="compact-xs"
                                variant="subtle"
                                onClick={() => onAdoptAllAsOverride!(focus!, overridable)}
                            >
                                Adopt all {overridable.length} as overrides
                            </Button>
                        </Tooltip>
                    )}
                </Group>
            )}

            {groups && groups.length > 0 && (
                <DiffGroups
                    groups={groups}
                    flow="source"
                    rowAction={
                        rowAction && diffFields.size > 0
                            ? (group) => (diffFields.has(group.field) ? rowAction(group) : null)
                            : undefined
                    }
                    headings={[
                        'Field',
                        <span key="env">
                            <span className="rgs-numeric">{focus}</span> now
                        </span>,
                        'Master',
                    ]}
                />
            )}

            {mode === 'compare' && notCompared.length > 0 && (
                <Text fz={11} c="dimmed">
                    {notComparedSentence(type, notCompared)}
                </Text>
            )}
        </Stack>
    );
};
